import { useState, useRef } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "./ui/dialog";
import { UploadCloud, FileCheck2, Loader2, CheckCircle2, X, Sparkles } from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { useUI } from "../context/UIContext";
import { analyzeBill } from "../lib/api";
import { CONTACT } from "../data/content";

const Row = ({ label, value }) =>
  value ? (
    <div className="flex justify-between gap-4 py-2.5 border-b border-slate-100">
      <span className="text-sm text-slate-500">{label}</span>
      <span className="text-sm font-semibold text-navy-900 text-right">{value}</span>
    </div>
  ) : null;

export const BillUploadDialog = () => {
  const { uploadOpen, setUploadOpen } = useUI();
  const [file, setFile] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const inputRef = useRef(null);

  const reset = () => {
    setFile(null);
    setForm({ name: "", email: "", phone: "" });
    setResult(null);
    setLoading(false);
  };

  const close = (v) => {
    setUploadOpen(v);
    if (!v) setTimeout(reset, 300);
  };

  const onFile = (f) => {
    if (!f) return;
    if (f.size > 10 * 1024 * 1024) {
      toast.error("Datei zu groß (max. 10 MB).");
      return;
    }
    setFile(f);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!file) return toast.error("Bitte laden Sie Ihre Rechnung hoch.");
    if (!form.name || !form.email) return toast.error("Bitte Name und E-Mail angeben.");
    setLoading(true);
    try {
      const data = await analyzeBill({ ...form, file });
      setResult(data);
      toast.success("Analyse abgeschlossen!");
    } catch (err) {
      toast.error("Etwas ist schiefgelaufen. Bitte versuchen Sie es erneut.");
    } finally {
      setLoading(false);
    }
  };

  const a = result?.analysis || {};

  return (
    <Dialog open={uploadOpen} onOpenChange={close}>
      <DialogContent
        className="max-w-2xl p-0 overflow-hidden border-0 rounded-3xl gap-0 max-h-[92vh] overflow-y-auto"
        data-testid="bill-upload-dialog"
      >
        <DialogTitle className="sr-only">Rechnung prüfen & Klarheit bekommen</DialogTitle>
        <DialogDescription className="sr-only">Laden Sie Ihre Strom- oder Gasrechnung hoch für eine kostenlose, persönliche Rechnungsprüfung.</DialogDescription>
        {/* Header */}
        <div className="bg-blue-700 px-8 pt-8 pb-7 text-white relative">
          <button
            onClick={() => close(false)}
            className="absolute right-5 top-5 h-9 w-9 grid place-items-center rounded-full bg-white/10 hover:bg-white/20 transition-colors"
            data-testid="dialog-close-btn"
          >
            <X className="h-4 w-4" />
          </button>
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-blue-100 mb-3">
            <Sparkles className="h-3.5 w-3.5" /> Kostenlose Rechnungsprüfung
          </div>
          <h3 className="text-2xl sm:text-3xl font-heading font-semibold">
            Rechnung prüfen &{" "}
            <span className="font-serif italic text-blue-100">Klarheit</span> bekommen
          </h3>
        </div>

        <div className="px-8 py-7 bg-white">
          {!result ? (
            <form onSubmit={submit} className="space-y-5" data-testid="bill-upload-form">
              {/* Dropzone */}
              <div
                onClick={() => inputRef.current?.click()}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => { e.preventDefault(); onFile(e.dataTransfer.files?.[0]); }}
                className={`cursor-pointer rounded-2xl border-2 border-dashed p-8 text-center transition-colors ${
                  file ? "border-blue-500 bg-blue-50" : "border-slate-300 hover:border-blue-400 hover:bg-slate-50"
                }`}
                data-testid="dropzone"
              >
                <input
                  ref={inputRef}
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png,.webp"
                  className="hidden"
                  onChange={(e) => onFile(e.target.files?.[0])}
                  data-testid="file-input"
                />
                {file ? (
                  <div className="flex items-center justify-center gap-3 text-blue-700">
                    <FileCheck2 className="h-6 w-6" />
                    <span className="font-medium truncate max-w-[280px]">{file.name}</span>
                  </div>
                ) : (
                  <>
                    <UploadCloud className="h-9 w-9 mx-auto text-slate-400 mb-3" />
                    <p className="font-medium text-navy-900">Rechnung hierher ziehen oder klicken</p>
                    <p className="text-xs text-slate-400 mt-1">PDF, JPG, PNG · max. 10 MB · DSGVO-konform</p>
                  </>
                )}
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <input
                  required
                  placeholder="Name *"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full bg-transparent border-b-2 border-slate-200 focus:border-blue-500 outline-none py-2.5 text-sm transition-colors"
                  data-testid="upload-name-input"
                />
                <input
                  required
                  type="email"
                  placeholder="E-Mail *"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="w-full bg-transparent border-b-2 border-slate-200 focus:border-blue-500 outline-none py-2.5 text-sm transition-colors"
                  data-testid="upload-email-input"
                />
              </div>
              <input
                placeholder="Telefon / WhatsApp (optional)"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                className="w-full bg-transparent border-b-2 border-slate-200 focus:border-blue-500 outline-none py-2.5 text-sm transition-colors"
                data-testid="upload-phone-input"
              />

              <button
                type="submit"
                disabled={loading}
                className="w-full rounded-full bg-blue-700 hover:bg-blue-600 disabled:opacity-60 text-white py-4 font-medium transition-colors flex items-center justify-center gap-2"
                data-testid="upload-submit-btn"
              >
                {loading ? (
                  <><Loader2 className="h-5 w-5 animate-spin" /> Analysiere Ihre Rechnung…</>
                ) : (
                  <>Rechnung kostenlos prüfen lassen</>
                )}
              </button>
              <p className="text-[11px] text-slate-400 text-center leading-relaxed">
                Mit dem Absenden stimmen Sie der Verarbeitung Ihrer Daten zur Analyse zu. Ihre Unterlagen werden vertraulich behandelt.
              </p>
            </form>
          ) : (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-5"
              data-testid="analysis-result"
            >
              <div className="flex items-center gap-2 text-blue-700">
                <CheckCircle2 className="h-6 w-6" />
                <span className="font-heading text-lg font-medium">Ihre Auswertung</span>
              </div>

              {(a.anbieter || a.jahresverbrauch_kwh || a.jahreskosten_eur) && (
                <div className="rounded-2xl bg-slate-50 p-5">
                  <Row label="Anbieter" value={a.anbieter} />
                  <Row label="Tarif" value={a.tarif} />
                  <Row label="Energieart" value={a.energieart} />
                  <Row label="Jahresverbrauch" value={a.jahresverbrauch_kwh ? `${a.jahresverbrauch_kwh} kWh` : null} />
                  <Row label="Arbeitspreis" value={a.arbeitspreis_ct_kwh ? `${a.arbeitspreis_ct_kwh} ct/kWh` : null} />
                  <Row label="Grundpreis" value={a.grundpreis_eur_jahr ? `${a.grundpreis_eur_jahr} €/Jahr` : null} />
                  <Row label="Jahreskosten" value={a.jahreskosten_eur ? `${a.jahreskosten_eur} €` : null} />
                </div>
              )}

              {a.einsparpotenzial_eur && (
                <div className="rounded-2xl bg-blue-700 text-white p-6 text-center">
                  <p className="text-xs uppercase tracking-[0.2em] text-blue-100 mb-1">Geschätztes Einsparpotenzial</p>
                  <p className="text-4xl font-heading font-semibold">≈ {a.einsparpotenzial_eur} € <span className="text-lg">/ Jahr</span></p>
                </div>
              )}

              {a.zusammenfassung && (
                <p className="text-sm text-slate-600 leading-relaxed">{a.zusammenfassung}</p>
              )}

              {a.empfehlungen?.length > 0 && (
                <ul className="space-y-2">
                  {a.empfehlungen.map((r, i) => (
                    <li key={i} className="flex gap-2 text-sm text-navy-900">
                      <CheckCircle2 className="h-4 w-4 text-blue-600 mt-0.5 shrink-0" /> {r}
                    </li>
                  ))}
                </ul>
              )}

              <div className="rounded-2xl border border-slate-200 p-5 text-sm text-slate-600">
                Kerstin Hemming prüft Ihr Ergebnis persönlich und meldet sich innerhalb von 24 Stunden mit Ihrem individuellen Angebot.
              </div>

              <div className="flex flex-wrap gap-3">
                <a
                  href={CONTACT.whatsapp}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 min-w-[140px] text-center rounded-full bg-emerald-600 hover:bg-emerald-500 text-white py-3.5 font-medium transition-colors"
                  data-testid="result-whatsapp-btn"
                >
                  Jetzt per WhatsApp
                </a>
                <button
                  onClick={reset}
                  className="flex-1 min-w-[140px] rounded-full border border-slate-300 hover:bg-slate-50 text-navy-900 py-3.5 font-medium transition-colors"
                  data-testid="result-reset-btn"
                >
                  Weitere Rechnung
                </button>
              </div>
            </motion.div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
